import React, { FC, useState, useEffect } from "react";

import { StepIndicator, Heading } from "@telia/teddy";
import classnames from "classnames";
import PAGES from "../../../../../../constants/pages";
import { ICartItem, StepIndicatorType } from "../../../../../../models";
import { isRunningOnClientSide } from "../../../../../../shared/util";
import { ConnectedAlert, ProgressBar } from "../../../../components";
import ConnectedShoppingCart from "../../../../navigation/components/organisms/ConnectedShoppingCart/ConnectedShoppingCart";
import ContinueSection from "../../molecules/ContinueSection/ContinueSection";
import DisclaimerCard from "../DisclaimerCard/DisclaimerCard";
import "./CheckoutPage.less";

interface CheckoutPageProps {
    children: React.ReactNode;
    className?: string;
    emptyOrder?: () => void;
    hasCreditCheckFailed?: boolean;
    hasSubscription?: boolean;
    initiatedBy?: string;
    isFetchingData: boolean;
    isKeepExisting?: boolean;
    isMBB?: boolean;
    isMinSideSimdealOrder?: boolean;
    isNavigating?: boolean;
    isReadyToContinue?: boolean;
    isReswitchAndHasNoMemberStep?: boolean;
    isTeliaTrygg?: boolean;
    lastAddedCartItem?: ICartItem;
    pageHeading?: string;
    pageIndex: number;
    path: string;
    resetBankId?: () => void;
    shouldOnlyShowSlideoutCart?: boolean;
    stepIndicator: StepIndicatorType;
    topSection?: React.ReactNode;
}

const CheckoutPage: FC<CheckoutPageProps> = ({
    className = "",
    isFetchingData,
    isTeliaTrygg = false,
    isNavigating,
    isReadyToContinue = false,
    isReswitchAndHasNoMemberStep,
    isMBB = false,
    isMinSideSimdealOrder = false,
    pageHeading,
    isKeepExisting,
    pageIndex,
    stepIndicator: { hide, steps, labels, links },
    path,
    hasSubscription,
    children,
    lastAddedCartItem,
    hasCreditCheckFailed,
    resetBankId,
    emptyOrder,
    shouldOnlyShowSlideoutCart,
    initiatedBy,
    topSection,
}) => {
    const [isClientSide, setIsClientSide] = useState(false);

    useEffect(() => {
        setIsClientSide(isRunningOnClientSide());
    }, []);

    return (
        <div
            className={classnames("beta-checkout-page", {
                [className]: className,
            })}
        >
            <div className="beta-checkout-page__heading-container">
                <span className="beta-checkout-page__step-indicator" data-cy="step-indicator">
                    {!hide && (
                        <div className="beta-checkout-page__step-indicator-wrapper">
                            <StepIndicator
                                index={Math.max(pageIndex - 1, 0)}
                                numberOfSteps={steps}
                                labels={labels}
                                links={links}
                                kind="purple"
                            />
                        </div>
                    )}
                </span>
                {pageHeading && (
                    <span className="beta-checkout-page__heading" data-cy="checkout-heading">
                        <Heading mb="100" as="h1" variant="title-600">
                            {pageHeading}
                        </Heading>
                    </span>
                )}
            </div>
            <ProgressBar isFetchingData={isFetchingData || isNavigating} standByColor="white" backgroundColor="white" />
            <ConnectedAlert />
            {topSection}
            <div className="beta-checkout-page__content">
                <div className="beta-checkout-page__column">
                    {children}
                    {!isKeepExisting && path !== PAGES.CHECKOUT.RECEIPT.URL && (
                        <ContinueSection
                            isReadyToContinue={isReadyToContinue}
                            isMBB={isMBB}
                            isReswitchAndHasNoMemberStep={isReswitchAndHasNoMemberStep}
                            hasSubscription={hasSubscription}
                            path={path}
                            lastAddedCartItem={lastAddedCartItem}
                            hasCreditCheckFailed={hasCreditCheckFailed}
                            initiatedBy={initiatedBy}
                            isMinSideSimdealOrder={isMinSideSimdealOrder}
                            resetBankId={resetBankId}
                            emptyOrder={emptyOrder}
                        />
                    )}
                    {isClientSide && window.location.pathname === PAGES.CHECKOUT.SUMMARY.URL && <DisclaimerCard />}
                    {isClientSide && window.location.pathname === PAGES.CHECKOUT.ADD_MEMBER.URL && isTeliaTrygg && (
                        <DisclaimerCard />
                    )}
                </div>

                {!shouldOnlyShowSlideoutCart && (
                    <div className="beta-checkout-page__column" data-cy="checkout-cart">
                        <div className="beta-checkout-page__cart-wrapper">
                            <ConnectedShoppingCart useStandalone={true} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CheckoutPage;
